import React, { useState } from 'react';
import { TrendingUp, Bot, MessageCircle, BedDouble, ArrowUpRight, ChevronLeft, ChevronRight, BarChart3 } from 'lucide-react';
import { Button } from '../ui/button';

const statisticsData = [
  {
    id: 'ai',
    title: 'AI Receptionist Impact',
    icon: Bot,
    color: '#10B981',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    description: 'What instant, 24/7 answering does for a service business',
    source: 'MIT/InsideSales lead-response studies; missed-call industry research 2025',
    stats: [
      { label: 'of callers who reach voicemail hang up without a message', value: '85%', trend: 'never call back' },
      { label: 'more likely to convert when you reply within 5 minutes', value: '21x', trend: 'vs 30+ minutes' },
      { label: 'of calls missed by small businesses during work hours', value: '20–35%', trend: '+ nearly all after hours' },
      { label: 'lower cost per customer interaction with AI vs human-only', value: '68%', trend: 'support cost studies' },
    ],
    chartData: [
      { month: 'Before', leads: 5, views: 100 },
      { month: 'Week 1', leads: 12, views: 250 },
      { month: 'Week 2', leads: 18, views: 380 },
      { month: 'Week 3', leads: 25, views: 520 },
      { month: 'Week 4', leads: 32, views: 680 },
      { month: 'Week 6', leads: 38, views: 820 },
      { month: 'Week 8', leads: 45, views: 950 },
    ],
    chartKeys: { primary: 'leads', secondary: 'views', secondaryScale: 0.1 },
    chartLabels: { primary: 'Qualified Leads', secondary: 'Enquiries Answered' },
    roi: {
      investment: '₦75,000/month',
      humanAlternative: '₦70,000–₦150,000+/month (one shift only)',
      coverage: '24/7/365',
      avgResponseTime: '< 60 seconds',
      breakEven: '1–2 saved bookings/month'
    }
  },
  {
    id: 'whatsapp',
    title: 'WhatsApp Automation Impact',
    icon: MessageCircle,
    color: '#25D366',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    description: 'Data on WhatsApp commerce in Nigeria and follow-up automation',
    source: 'Statista/DataReportal Nigeria 2025; Invesp & ZoomInfo follow-up research',
    stats: [
      { label: 'of Nigerian internet users are on WhatsApp', value: '95%+', trend: '~51M active users' },
      { label: 'of customers buy from the business that replies first', value: '78%', trend: 'speed wins' },
      { label: 'of sales require 5+ follow-ups after first contact', value: '80%', trend: 'persistence pays' },
      { label: 'of businesses give up after just one follow-up', value: '44%', trend: 'the gap you can close' },
    ],
    chartData: [
      { month: 'Before', withCRM: 100, withoutCRM: 100 },
      { month: 'Week 1', withCRM: 95, withoutCRM: 75 },
      { month: 'Week 2', withCRM: 90, withoutCRM: 55 },
      { month: 'Week 4', withCRM: 87, withoutCRM: 40 },
      { month: 'Week 8', withCRM: 85, withoutCRM: 32 },
      { month: 'Week 12', withCRM: 82, withoutCRM: 28 },
    ],
    chartKeys: { primary: 'withCRM', secondary: 'withoutCRM', secondaryScale: 1 },
    chartLabels: { primary: 'With Automated Follow-Up', secondary: 'Without Follow-Up' },
    roi: {
      investment: '₦60,000/month',
      metaFees: 'Under ₦10 per reminder message',
      marketingMessage: '~₦80 per promotional message',
      speedToLead: 'Under 60 seconds',
      breakEven: '1–2 saved sales/month'
    }
  },
  {
    id: 'shortlet',
    title: 'Shortlet Automation Impact',
    icon: BedDouble,
    color: '#EC4899',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-200',
    description: 'Why response speed decides who gets the booking in Nigerian shortlets',
    source: 'Jiji/Nigeria Property Centre market data 2025–2026; Parikh et al. reminder study',
    stats: [
      { label: 'average nightly rate for a 2-bedroom shortlet in Lekki', value: '₦200k', trend: 'Lagos premium areas' },
      { label: 'reduction in no-shows with automated confirmations & reminders', value: '38%', trend: '13,500-appointment study' },
      { label: 'more likely to convert an enquiry by replying in under 5 minutes', value: '21x', trend: 'guests message multiple hosts' },
      { label: 'of nights stayed booked via DM/WhatsApp-first enquiries', value: 'Most', trend: 'Nigerian booking culture' },
    ],
    chartData: [
      { month: 'Before', leads: 5, views: 100 },
      { month: 'Month 1', leads: 14, views: 300 },
      { month: 'Month 2', leads: 20, views: 440 },
      { month: 'Month 3', leads: 28, views: 620 },
      { month: 'Month 4', leads: 34, views: 760 },
      { month: 'Month 5', leads: 40, views: 880 },
      { month: 'Month 6', leads: 47, views: 1020 },
    ],
    chartKeys: { primary: 'leads', secondary: 'views', secondaryScale: 0.1 },
    chartLabels: { primary: 'Bookings/Month', secondary: 'Enquiries Captured' },
    roi: {
      investment: '₦100,000/month',
      avgNightRate: '₦100,000–₦200,000',
      nightsToBreakEven: '1–2 extra nights',
      occupancyGain: '+15–30% typical',
      peakSeason: 'Detty December ready'
    }
  }
];

const Statistics = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeData = statisticsData[activeIndex];
  const Icon = activeData.icon;

  const nextStat = () => {
    setActiveIndex((prev) => (prev + 1) % statisticsData.length);
  };

  const prevStat = () => {
    setActiveIndex((prev) => (prev - 1 + statisticsData.length) % statisticsData.length);
  };

  // Calculate max values for chart scaling
  const maxChartValue = Math.max(...activeData.chartData.map(d =>
    Math.max(...Object.values(d).filter(v => typeof v === 'number'))
  ));

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-amber-100 text-amber-600 text-sm font-medium mb-6">
            Research-Backed Results
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Data That Proves{' '}
            <span className="text-emerald-500">Real Impact</span>
          </h2>
          <p className="text-xl text-gray-600">
            Industry statistics and research data showing the proven effectiveness of AI answering, booking and follow-up.
          </p>
        </div>

        {/* Service Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {statisticsData.map((service, index) => {
            const ServiceIcon = service.icon;
            return (
              <button
                key={service.id}
                onClick={() => setActiveIndex(index)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                  index === activeIndex
                    ? `${service.bgColor} ${service.borderColor} border-2 shadow-lg`
                    : 'bg-gray-100 border-2 border-transparent hover:bg-gray-200'
                }`}
                style={index === activeIndex ? { color: service.color } : {}}
              >
                <ServiceIcon className="w-5 h-5" />
                {service.title}
              </button>
            );
          })}
        </div>

        {/* Main Statistics Display */}
        <div className="relative max-w-6xl mx-auto">
          <div className={`${activeData.bgColor} rounded-3xl p-8 md:p-12 border-2 ${activeData.borderColor}`}>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-10">
              <div className="flex items-center gap-4 mb-4 md:mb-0">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: activeData.color }}
                >
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{activeData.title}</h3>
                  <p className="text-gray-600">{activeData.description}</p>
                </div>
              </div>
              <div className="text-sm text-gray-500 bg-white px-4 py-2 rounded-lg">
                Source: {activeData.source}
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              {activeData.stats.map((stat, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-start justify-between mb-2">
                    <span
                      className="text-4xl font-bold"
                      style={{ color: activeData.color }}
                    >
                      {stat.value}
                    </span>
                    <span className="flex items-center text-xs text-emerald-600 bg-emerald-100 px-2 py-1 rounded-full">
                      <ArrowUpRight className="w-3 h-3 mr-1" />
                      {stat.trend}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Chart and ROI Section */}
            {/* [&>*]:min-w-0 — a grid item's automatic minimum size is its
                min-content width, so the chart's bar labels forced a 331px
                track inside a 275px column on a phone and the whole page could
                pan sideways. Clearing it lets the track follow the container. */}
            <div className="grid lg:grid-cols-2 gap-8 [&>*]:min-w-0">
              {/* Chart */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-6 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" style={{ color: activeData.color }} />
                  Typical Growth Trajectory
                </h4>
                <div className="h-64 flex items-end justify-between gap-2">
                  {activeData.chartData.map((data, index) => {
                    const primaryValue = data[activeData.chartKeys.primary];
                    const secondaryValue = data[activeData.chartKeys.secondary] * (activeData.chartKeys.secondaryScale || 1);

                    return (
                      <div key={index} className="flex-1 min-w-0 flex flex-col items-center gap-2">
                        <div className="w-full flex gap-1 items-end h-48">
                          <div
                            className="flex-1 rounded-t-lg transition-all duration-500"
                            style={{
                              height: `${(primaryValue / maxChartValue) * 100}%`,
                              backgroundColor: activeData.color,
                              minHeight: '8px'
                            }}
                          />
                          <div
                            className="flex-1 rounded-t-lg transition-all duration-500 opacity-40"
                            style={{
                              height: `${(secondaryValue / maxChartValue) * 100}%`,
                              backgroundColor: activeData.color,
                              minHeight: '8px'
                            }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 text-center">{data.month}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-center gap-6 mt-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: activeData.color }} />
                    <span className="text-gray-600">{activeData.chartLabels.primary}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded opacity-40" style={{ backgroundColor: activeData.color }} />
                    <span className="text-gray-600">{activeData.chartLabels.secondary}</span>
                  </div>
                </div>
              </div>

              {/* ROI Calculator */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-6 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" style={{ color: activeData.color }} />
                  The Numbers For Your Business
                </h4>
                <div className="space-y-4">
                  {Object.entries(activeData.roi).map(([key, value], index) => (
                    <div key={index} className="flex justify-between items-center py-3 border-b border-gray-100 last:border-0">
                      <span className="text-gray-600 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                      <span
                        className="font-bold text-lg text-right"
                        style={{ color: ['investment'].includes(key) ? activeData.color : '#111827' }}
                      >
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 rounded-xl" style={{ backgroundColor: `${activeData.color}15` }}>
                  <p className="text-sm text-gray-700">
                    <strong>Key Insight:</strong> {
                      activeData.id === 'ai'
                        ? 'The business that answers first usually wins the customer — and AI answers in seconds, every time, around the clock.'
                        : activeData.id === 'whatsapp'
                        ? 'With 95%+ of Nigerian internet users on WhatsApp and 80% of sales needing 5+ touches, automated follow-up is the highest-leverage system a Nigerian business can build.'
                        : 'In Nigerian shortlets, guests message several hosts at once and book whoever replies first — availability answers at 2 AM are worth real money during Detty December.'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-center gap-4 mt-8">
            <Button
              variant="outline"
              size="icon"
              onClick={prevStat}
              className="w-12 h-12 rounded-full border-gray-300 hover:border-emerald-500 hover:bg-emerald-50"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              {statisticsData.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === activeIndex
                      ? 'bg-emerald-500 w-8'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={nextStat}
              className="w-12 h-12 rounded-full border-gray-300 hover:border-emerald-500 hover:bg-emerald-50"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-4">
            Ready to see these numbers in your own business?
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 text-white font-semibold rounded-full hover:bg-emerald-600 transition-colors"
          >
            Get Started Today
            <ArrowUpRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Statistics;
